import { Box, Button, Flex, Heading, List, ListItem, Text } from '@opendoor/bricks/core';
import React, { useState } from 'react';
import { CategoryForm } from 'sellDirect/components/closingDash/disclosures/DynamicDisclosures/CategoryForm';
import { PromptGroup, getInitialStepNumber } from 'sellDirect/helpers/closingDash/disclosures';
import { CloseButtonProps, StepInfo, Wizard } from 'shared/components/Wizard';
import { ReactComponent as CelebrationIcon } from 'static/images/celebration.svg';

const WIZARD_TITLE = 'Disclosures';

export interface QuestionnaireWizardProps {
  questionCategories: PromptGroup[];
  submit: () => void;
  saveProgress: () => void;
  onExit: () => void;
}

/**
 * Renders new question wizard based on question categories
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const QuestionnaireWizard: React.FC<QuestionnaireWizardProps> = ({
  questionCategories,
  saveProgress,
  submit,
  onExit,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  // called when Save and close button is clicked to save progress of current responses
  const onSaveAndExit = () => {
    saveProgress();
    onExit();
  };

  // called to submit responses
  const onSubmit = () => {
    setIsSubmitted(true);
    submit();
  };

  const categoryForms: StepInfo[] = questionCategories.map((questionCategory, index) => {
    const isLastStepToFillDetails = index + 1 == questionCategories.length;
    return {
      title: `${WIZARD_TITLE}: ${index + 1} of ${questionCategories.length}`,
      // eslint-disable-next-line react/display-name
      content: ({ goToNextStep }) => (
        <CategoryForm
          questionCategory={questionCategory}
          isSubmitStep={isLastStepToFillDetails}
          saveProgress={saveProgress}
          onSubmitStep={() => {
            if (isLastStepToFillDetails) {
              onSubmit();
            }
            goToNextStep();
          }}
        />
      ),
    };
  });

  const startStep: StepInfo = {
    title: WIZARD_TITLE,
    // eslint-disable-next-line react/display-name
    content: ({ goToNextStep }) => (
      <Flex
        flexDirection="column"
        alignItems="center"
        mx="24"
        my="32"
        minWidth="327"
        maxWidth="400"
        gap="40"
      >
        <Box paddingX="40" paddingY="32" backgroundColor="neutrals10">
          <Heading as="h3">Seller disclosure</Heading>
          <Text fontSize="s1" mt="16">
            State laws require sellers provide details about the property's condition.
          </Text>
          <List variant="bullet" mt="32">
            <ListItem>
              Your responses won't impact the sale price or contract with Opendoor.
            </ListItem>
            <ListItem>
              Opendoor will provide this information to future buyers when we sell the home.
            </ListItem>
            <ListItem>Please answer the questions to the best of your knowledge.</ListItem>
          </List>
        </Box>
        <Button
          analyticsName={`${WIZARD_TITLE.toLowerCase()}-start`}
          aria-label={`${WIZARD_TITLE.toLowerCase()}-start`}
          onClick={goToNextStep}
        >
          Start
        </Button>
      </Flex>
    ),
  };

  const endStep: StepInfo = {
    title: WIZARD_TITLE,
    // eslint-disable-next-line react/display-name
    content: () => (
      <Flex
        flexDirection="column"
        alignItems="center"
        mx="24"
        my="32"
        minWidth="327"
        maxWidth="400"
        gap="40"
      >
        <Box minWidth="327" maxWidth="400" mt="48" mb="108">
          <CelebrationIcon />
          <Text fontSize="s4" fontWeight="bold" my="30" whiteSpace="nowrap">
            Disclosures submitted!
          </Text>

          <Text>
            Thank you for submitting these disclosures. We’ll be in touch with any questions.
          </Text>
        </Box>
        <Button
          minWidth="327"
          maxWidth="400"
          analyticsName={`${WIZARD_TITLE.toLowerCase()}-close`}
          aria-label={`${WIZARD_TITLE.toLowerCase()}-close`}
          onClick={onExit}
        >
          Close
        </Button>
      </Flex>
    ),
    hideGoBack: true,
  };

  const saveAndCloseButtonProps: CloseButtonProps = {
    analyticsName: `${WIZARD_TITLE.toLowerCase()}-save`,
    label: 'save-and-exit',
    isVisible: !isSubmitted,
    onClick: onSaveAndExit,
    nameOverride: (currentStepIndex: number) => (currentStepIndex === 0 ? 'Exit' : 'Save and exit'),
  };

  const allSteps = [startStep, ...categoryForms, endStep];

  return (
    <Wizard
      steps={allSteps}
      title={WIZARD_TITLE}
      initialStepNumber={getInitialStepNumber(questionCategories)}
      onClose={onExit}
      closeButtonProps={saveAndCloseButtonProps}
    />
  );
};
